import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, Stack } from '@mui/material';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadThreatMonitoring, changeThreatEventStatus } from './threatMonitoringSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const SEV_COLOR = { low: tokens.severity.low, medium: tokens.severity.medium, high: tokens.severity.high, critical: tokens.severity.critical };
const STATUS_COLOR = { new: tokens.severity.info, acknowledged: tokens.severity.medium, escalated: tokens.severity.critical, dismissed: tokens.text.muted };

export default function ThreatMonitoringPage() {
  const dispatch = useDispatch();
  const { items } = useSelector((s) => s.threatMonitoring);

  useEffect(() => { dispatch(loadThreatMonitoring()); }, [dispatch]);

  const newCount = items.filter((e) => e.status === 'new').length;
  const escalated = items.filter((e) => e.status === 'escalated').length;

  return (
    <Box>
      <PageHeader
        title="Threat Monitoring"
        subtitle="Triage workflow over the raw Threat Detection feed — acknowledge, escalate to an Incident, or dismiss"
      />
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Events (24h)" value={items.length} icon={RadarRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="New / Untriaged" value={newCount} accent={tokens.severity.info} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Escalated" value={escalated} accent={tokens.severity.critical} /></Grid>
      </Grid>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Title', 'Category', 'Severity', 'Source IP', 'Status', 'Detected', 'Actions'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell sx={{ fontSize: '0.8rem', maxWidth: 260 }}>{e.title}</TableCell>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', color: tokens.text.muted }}>{e.detectionCategory}</TableCell>
                <TableCell><Chip label={e.severity} size="small" sx={{ color: SEV_COLOR[e.severity], border: `1px solid ${SEV_COLOR[e.severity]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.74rem' }}>{e.sourceIp}</TableCell>
                <TableCell><Chip label={e.status} size="small" sx={{ color: STATUS_COLOR[e.status], border: `1px solid ${STATUS_COLOR[e.status]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(e.detectedAt)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {e.status === 'new' && (
                      <Button size="small" onClick={() => dispatch(changeThreatEventStatus({ id: e.id, status: 'acknowledged' }))}>Ack</Button>
                    )}
                    {e.status !== 'escalated' && e.status !== 'dismissed' && (
                      <Button size="small" color="error" onClick={() => dispatch(changeThreatEventStatus({ id: e.id, status: 'escalated' }))}>Escalate</Button>
                    )}
                    {e.status !== 'dismissed' && e.status !== 'escalated' && (
                      <Button size="small" onClick={() => dispatch(changeThreatEventStatus({ id: e.id, status: 'dismissed' }))}>Dismiss</Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
